import { useId, useMemo, useState } from 'react';
import type React from 'react';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';

export type ComboboxFieldOption = {
  value: string;
  label: string;
};

type ComboboxFieldProps = {
  options: ComboboxFieldOption[];
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  value?: string | string[] | null;
  defaultValue?: string | string[] | null;
  onChange?: (value: string | string[] | null) => void;
};

export const ComboboxField: React.FC<ComboboxFieldProps> = ({
  options,
  multiple = false,
  label,
  placeholder = 'Select...',
  emptyMessage = 'No items found.',
  disabled,
  value: valueProp,
  defaultValue,
  onChange,
}) => {
  const inputId = useId();
  const [internalValue, setInternalValue] = useState<string | string[] | null>(
    defaultValue ?? (multiple ? [] : null)
  );
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const selectedOptions = useMemo(() => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      return options.filter((option) => values.includes(option.value));
    } else if (value === null) {
      return null;
    } else {
      return options.find((option) => option.value === value) ?? null;
    }
  }, [options, value, multiple]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Combobox
        items={options}
        value={selectedOptions}
        multiple={multiple}
        onValueChange={(nextValue) => {
          if (multiple) {
            const nextOptions = Array.isArray(nextValue) ? nextValue : [];
            const nextValues = nextOptions.map((option) => option.value);
            if (!isControlled) setInternalValue(nextValues);
            onChange?.(nextValues);
            return;
          }

          const option = nextValue as ComboboxFieldOption | null;
          const nextValueString = option?.value ?? null;
          if (!isControlled) setInternalValue(nextValueString);
          onChange?.(nextValueString);
        }}
        itemToStringLabel={(option) => option.label}
        itemToStringValue={(option) => option.value}
        isItemEqualToValue={(a, b) => a.value === b.value}
        disabled={disabled}
      >
        {multiple ? (
          <ComboboxChips>
            <ComboboxValue>
              {(selectedOptions as ComboboxFieldOption[]).map((option) => (
                <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
              ))}
            </ComboboxValue>
            <ComboboxChipsInput
              id={inputId}
              placeholder={placeholder}
              disabled={disabled}
            />
          </ComboboxChips>
        ) : (
          <ComboboxInput
            id={inputId}
            placeholder={placeholder}
            disabled={disabled}
            showClear
          />
        )}
        <ComboboxContent>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {(option) => (
              <ComboboxItem key={option.value} value={option}>
                {option.label}
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
